import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowRight, Building2, CalendarCheck, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { SeqLogo } from "@/components/SeqLogo";

const DEMO = [
  { role: "Administrator", email: "admin@demo.ie", password: "Admin123!", fin: true },
  { role: "Scheduler / Manager", email: "scheduler@demo.ie", password: "Schedule123!", fin: false },
  { role: "Consultant", email: "consultant@demo.ie", password: "Consult123!", fin: false },
  { role: "Read Only", email: "readonly@demo.ie", password: "Readonly123!", fin: false },
  { role: "Senior Management", email: "management@demo.ie", password: "Manage123!", fin: true },
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav(user.is_superadmin ? "/platform/accounts" : "/dashboard", { replace: true });
  }, [user, nav]);
  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.is_superadmin ? "/platform/accounts" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const quick = (d) => { setEmail(d.email); setPassword(d.password); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-seq-forest p-12 relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-seq-green/10" />
        <div className="absolute -left-16 bottom-10 w-72 h-72 rounded-full bg-seq-teal/10" />
        <div className="relative">
          <SeqLogo />
        </div>
        <div className="relative">
          <h1 className="text-4xl font-display font-bold text-white leading-tight">
            Operational management,<br /><span className="text-seq-green">delivered end to end.</span>
          </h1>
          <p className="text-slate-200/70 mt-4 max-w-md">
            Clients, sites, contracts, recurring jobs, consultant scheduling, completion and management
            reporting — a single professional platform, powered by Amden Control.
          </p>
          <div className="mt-8 space-y-3">
            {[[Building2, "Multi-site client & contract management"], [CalendarCheck, "Intelligent scheduling & consultant availability"], [BarChart3, "Role-based dashboards with financial controls"]].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-slate-100/90">
                <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center text-seq-green"><Icon size={18} /></div>
                <span className="text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-slate-300/40 text-xs">© 2026 Amden Control</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center">
            <div className="bg-seq-forest rounded-xl px-4 py-3"><SeqLogo /></div>
          </div>
          <h2 className="text-2xl font-display font-semibold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Access your Amden Control workspace.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-seq-green focus:ring-2 focus:ring-seq-green/20 outline-none" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Password</label>
              <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-seq-green focus:ring-2 focus:ring-seq-green/20 outline-none" />
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={busy}
              className="w-full bg-seq-green hover:bg-seq-greenhover text-white font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <>Sign in <ArrowRight size={18} /></>}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button data-testid="login-google-btn" onClick={google}
            className="w-full border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
            Continue with Google
          </button>

          <div className="mt-7 border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
              <ShieldCheck size={15} className="text-seq-green" />
              <span className="text-xs font-semibold text-slate-600">Demo accounts — click to fill</span>
            </div>
            <div className="divide-y divide-slate-100">
              {DEMO.map((d) => (
                <button key={d.email} onClick={() => quick(d)} data-testid={`demo-fill-${d.role.split(" ")[0].toLowerCase()}`}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left">
                  <div>
                    <div className="text-sm font-medium text-slate-700">{d.role}</div>
                    <div className="text-xs text-slate-400">{d.email}</div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${d.fin ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {d.fin ? "Financial" : "No financial"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
